/**
 * YUMIX — Day8PriorityBlock
 *
 * Rendered just below the pilot-profile header on the Dashboard. Surfaces
 * the 3 universal pillars Yuri (2026-05-06 spec) wants front-and-center
 * for every athlete-path user:
 *
 *   Mon Plan · Nutrition · Social & Défis
 *
 * Lifestyle path keeps a softer trio (Sommeil / Stress / Routines).
 *
 * Returns null if no day8_path is set yet (J7 modal hasn't fired).
 *
 * Iter. 411.x — Tiles migrated to unified GLOW aesthetic (dark glass +
 * color-specific neon border/glow/text-shadow). Every tile now shares the
 * same visual language as FAB, `Comme hier`, RankUpModal, etc.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Dumbbell, Heart, Moon, Brain, ChevronRight, Sparkles, Apple, Trophy } from 'lucide-react';

// GLOW palette per color key. Keep `rgb` as `r,g,b` strings so consumers
// can drop them straight into rgba() shadows.
const GLOW = {
  orange: { rgb: '251,146,60', text: '#fed7aa', label: '#fef3c7', icon: '#fed7aa' },
  violet: { rgb: '167,139,250', text: '#ddd6fe', label: '#ede9fe', icon: '#ddd6fe' },
  cyan:   { rgb: '34,211,238',  text: '#a5f3fc', label: '#cffafe', icon: '#a5f3fc' },
};

const Tile = ({ icon: Icon, label, sublabel, onClick, color = 'orange', delay = 0, testid }) => {
  const g = GLOW[color] || GLOW.orange;
  return (
    <motion.button
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      onClick={onClick}
      data-testid={testid}
      className="group flex items-center gap-3 rounded-xl p-3 text-left transition-all hover:scale-[1.02] active:scale-[0.98]"
      style={{
        background: `linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(${g.rgb},0.20) 100%)`,
        border: `1px solid rgba(${g.rgb},0.45)`,
        boxShadow: `0 0 16px rgba(${g.rgb},0.38), inset 0 1px 0 rgba(255,255,255,0.05)`,
        backdropFilter: 'blur(6px)',
      }}
    >
      <div
        className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
        style={{
          background: `linear-gradient(140deg, rgba(${g.rgb},0.45), rgba(${g.rgb},0.18))`,
          boxShadow: `0 0 12px rgba(${g.rgb},0.55), inset 0 0 0 1px rgba(${g.rgb},0.5)`,
        }}
      >
        <Icon
          className="w-5 h-5"
          style={{ color: g.icon, filter: `drop-shadow(0 0 6px rgba(${g.rgb},0.9))` }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="font-heading text-sm uppercase tracking-tight truncate font-black"
          style={{ color: g.label, textShadow: `0 0 6px rgba(${g.rgb},0.5)` }}
        >
          {label}
        </p>
        <p
          className="text-[11px] truncate"
          style={{ color: `rgba(${g.rgb},0.75)` }}
        >
          {sublabel}
        </p>
      </div>
      <ChevronRight
        className="w-4 h-4 transition-opacity opacity-70 group-hover:opacity-100"
        style={{ color: g.text, filter: `drop-shadow(0 0 4px rgba(${g.rgb},0.7))` }}
      />
    </motion.button>
  );
};

const Day8PriorityBlock = ({ user, language = 'fr' }) => {
  const fr = (language || 'fr').startsWith('fr');
  const navigate = useNavigate();
  const path = user?.day8_path;
  if (!path) return null;

  const isAthlete = path === 'athlete';
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-5"
      data-testid="day8-priority-block"
      data-path={path}
    >
      <div className="flex items-center gap-2 mb-2.5">
        <Sparkles
          className="w-3.5 h-3.5"
          style={{
            color: isAthlete ? '#fed7aa' : '#ddd6fe',
            filter: isAthlete
              ? 'drop-shadow(0 0 5px rgba(251,146,60,0.9))'
              : 'drop-shadow(0 0 5px rgba(167,139,250,0.9))',
          }}
        />
        <h2
          className="font-heading text-xs uppercase tracking-[0.25em] font-black"
          style={{
            color: isAthlete ? '#fed7aa' : '#ddd6fe',
            textShadow: isAthlete
              ? '0 0 6px rgba(251,146,60,0.55)'
              : '0 0 6px rgba(167,139,250,0.55)',
          }}
        >
          {fr ? 'Ton parcours en priorité' : 'Your path · priority'}
        </h2>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {isAthlete ? (
          <>
            <Tile
              icon={Dumbbell}
              label={fr ? 'Mon plan' : 'My plan'}
              sublabel={fr ? 'Programme IA actif' : 'Active AI program'}
              color="orange"
              onClick={() => navigate('/workouts?tab=plan')}
              testid="priority-athlete-myplan"
              delay={0.05}
            />
            <Tile
              icon={Apple}
              label={fr ? 'Nutrition' : 'Nutrition'}
              sublabel={fr ? 'Suivi de mes repas' : 'Track my meals'}
              color="orange"
              onClick={() => navigate('/meals')}
              testid="priority-athlete-nutrition"
              delay={0.1}
            />
            <Tile
              icon={Trophy}
              label={fr ? 'Social & Défis' : 'Social & Challenges'}
              sublabel={fr ? 'Communauté · Légendes · Clash' : 'Community · Legends · Clash'}
              color="orange"
              onClick={() => navigate('/clash')}
              testid="priority-athlete-social"
              delay={0.15}
            />
            <Tile
              icon={Brain}
              label={fr ? 'Mindset' : 'Mindset'}
              sublabel={fr ? 'Réflexion du jour' : "Today's reflection"}
              color="orange"
              onClick={() => navigate('/mindset')}
              testid="priority-athlete-mindset"
              delay={0.2}
            />
          </>
        ) : (
          <>
            <Tile
              icon={Moon}
              label={fr ? 'Sommeil' : 'Sleep'}
              sublabel={fr ? 'Tracker + recommandations' : 'Tracker + insights'}
              color="violet"
              onClick={() => navigate('/sleep')}
              testid="priority-lifestyle-sleep"
              delay={0.05}
            />
            <Tile
              icon={Brain}
              label={fr ? 'Mindset' : 'Mindset'}
              sublabel={fr ? 'Réflexion du jour' : "Today's reflection"}
              color="orange"
              onClick={() => navigate('/mindset')}
              testid="priority-lifestyle-mindset"
              delay={0.1}
            />
            <Tile
              icon={Sparkles}
              label={fr ? 'Stress' : 'Stress'}
              sublabel={fr ? 'Cohérence cardiaque · bientôt' : 'HRV · coming soon'}
              color="violet"
              onClick={() => navigate('/stress')}
              testid="priority-lifestyle-stress"
              delay={0.15}
            />
            <Tile
              icon={Heart}
              label={fr ? 'Routines' : 'Routines'}
              sublabel={fr ? 'Body scan · respiration' : 'Body scan · breathing'}
              color="violet"
              onClick={() => navigate('/sleep')}
              testid="priority-lifestyle-routines"
              delay={0.2}
            />
          </>
        )}
      </div>
    </motion.section>
  );
};

export default Day8PriorityBlock;
